import React, { useEffect, useState } from 'react';
import { Checkbox, Radio, Avatar, Stack, Box, Button, Typography, Paper } from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';
import ProgressBar from '../components/progressbar';
import { useAuth } from '../contexts/AuthContext';
import { useGameUtils } from '../utilities/GameUtils';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useApi } from '../services/api';
import palette from '../assets/sass/palette.scss';

const GamePoll = ({ gameData, onVote, onConfirm }) => {


  const { db } = useFirebase();
  const { currentUser } = useAuth();
  const isOrganiser = currentUser && currentUser.uid === gameData.createdBy;
  const [selectedDate, setSelectedDate] = useState('');
  const [acceptedDates, setAcceptedDates] = useState([]);
  const [dateTimeOptionsWithPhotoURL, setDateTimeOptionsWithPhotoURL] = useState([]);
  const [userNames, setUserNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasDateBeenSelected, setHasDateBeenSelected] = useState(false);
  const { getAllUserDetailsByUserIds } = useGameUtils();
  const { post } = useApi();
  const pollEnabled = () => {
    return true;
  };


  useEffect(() => {
    setHasDateBeenSelected(!!selectedDate);
  }, [selectedDate]);

  const calcPercentage = (dt) => {
    const votesForThisDate = (dt.acceptedBy || []).length;
    
    // Calculate total unique users who voted on any date
    const allVoters = new Set();
    gameData.dateTimeOptions.forEach((option) => {
      (option.acceptedBy || []).forEach((voter) => {
        allVoters.add(voter.uid);
      });
    });
    
    const totalVoters = allVoters.size;
    if (votesForThisDate === 0 || totalVoters === 0) return 0;
    
    return Math.floor((votesForThisDate / totalVoters) * 100);
  };

  const outputSelectedDate = (date) => {
    if (date && date.toDate) {
      return format(date.toDate(), "EEEE d MMM 'at\u00A0' h:mmaaa");
    }
    return 'TDB';
  };

  const getUserPhotoURL = (uid) => {
    const user = dateTimeOptionsWithPhotoURL?.find(
      (user) => user?.uid === uid || user?.user === uid
    );
    return user ? user?.photoURL : null;
  };

  const getUserInitial = (uid) => {
    const user = userNames?.find((user) => user?.uid === uid || user?.user === uid);
    return user ? user?.initial : '';
  };

  const handleSelectedDateChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedDate(selectedValue);
  };

  const hasUserSelectedDate = (idx) => {
    return acceptedDates[idx] || false;
  };

  const handleSelectedDateOption = (event) => {
    const idx = event.target.value;
    setAcceptedDates((prevAcceptedDates) => {
      const newAcceptedDates = [...prevAcceptedDates];
      newAcceptedDates[idx] = !newAcceptedDates[idx];
      return newAcceptedDates;
    });
  };

  const handleConfirmDate = async () => {
    setIsLoading(true);
    const dateTimeOption = gameData.dateTimeOptions.find((dt) => String(dt.id) === String(selectedDate));

    gameData.players = gameData.players || [];
    gameData.date = dateTimeOption.option;
    gameData.dateId = selectedDate;
    gameData.status = 'confirmed';

    // dateTimeOption.acceptedBy.forEach((inv) => {
    //   gameData.players.push({
    //     user: inv.uid,
    //     userName: inv.userName || '',
    //     score: 0,
    //     paid: false,
    //   });
    // });

    const gameRef = doc(db, 'games', gameData.id);
    await updateDoc(gameRef, gameData);
    await post('/game/game-confirmed', {
      gameId: gameData.id,
    });
    if (onConfirm) {
      onConfirm(gameData);
    }
    setIsLoading(false);
  };



  const getAllUserNames = async (gameData) => {
    const allDatesTimeOptions = gameData.dateTimeOptions.map((dt) => dt.acceptedBy).flat();
    const uidList = [];
    allDatesTimeOptions.forEach((option) => {
      uidList.push(option.uid);
    });
    gameData?.invitations?.forEach((option) => {
      uidList.push(option.user);
    });

    const userDetails = await getAllUserDetailsByUserIds(uidList);
    return userDetails;
  };

  const init = async () => {
    gameData.dateTimeOptions.map((dt) => {
      if (!dt.acceptedDates || dt.acceptedDates.length === 0) acceptedDates[dt.id] = false;
      for (let j = 0; j < (dt.acceptedBy || []).length; j++) {
        if (currentUser && dt.acceptedBy[j].uid === currentUser.uid) {
          acceptedDates[dt.id] = true;
          break;
        } else {
          acceptedDates[dt.id] = false;
        }
      }
      return dt;
    });
    setAcceptedDates(acceptedDates);

    const flatDateTimeOptions = gameData.dateTimeOptions.map((dt) => dt.acceptedBy).flat();
    const dateTimeOptionsWithPhotoURL = async () => {
      const dateTimeOptions = await Promise.all(
        flatDateTimeOptions.map(async (dateTimeOption) => {
          const userRef = doc(db, 'users', dateTimeOption.uid);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists ? userDoc.data() : {};
          return {
            ...dateTimeOption,
            photoURL: userData?.profileImage || null,
          };
        })
      );
      setDateTimeOptionsWithPhotoURL(dateTimeOptions);
    };
    dateTimeOptionsWithPhotoURL();

    const userNames = await getAllUserNames(gameData);
    setUserNames(userNames);
  };

  useEffect(() => {
    if (gameData) {
      init();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameData]);

  return (
    <Paper elevation={0} sx={{ mt: 5 }} variant="gameBox">
      NEW POLL
      {isOrganiser ? (
        <Typography variant="h6">
          Select a date to finalise the event, then start organising your match.
        </Typography>
      ) : (
        <Typography variant="moduleHeader" component="h5">
          Poll and Availability
        </Typography>
      )}
      <Stack>
        {(gameData.dateTimeOptions || []).map((dt) => (
          <Stack key={dt.id}>
            <Stack direction="row" alignItems="center">
              {isOrganiser && (
                <>
                  <Radio
                    disabled={!pollEnabled()}
                    checked={String(selectedDate) === String(dt.id)}
                    onChange={handleSelectedDateChange}
                    value={String(dt.id)}
                    name="radio-buttons"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<RadioButtonCheckedIcon />}
                    sx={{ color: palette.grey }}
                  />
                </>
              )}
              {!isOrganiser && (
                <Checkbox
                  disabled={!pollEnabled()}
                  value={dt.id}
                  checked={hasUserSelectedDate(dt.id)}
                  onChange={handleSelectedDateOption}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleOutlineIcon />}
                  sx={{ color: palette.grey }}
                />
              )}
              <Stack direction="row" sx={{ flexGrow: 1 }} alignItems="center">
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">{outputSelectedDate(dt.option)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ textAlign: 'right', color: 'grey' }}>
                    {calcPercentage(dt)}%
                  </Typography>
                </Box>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center">
              <ProgressBar progress={calcPercentage(dt)} color={palette.greenSharp} />
            </Stack>
            <Box sx={{ display: 'flex', gap: 2, paddingTop: 1 }}>
              {(dt.acceptedBy || []).map((vote, index) => {
                const initial = getUserInitial(vote?.uid);

                if (getUserPhotoURL(vote?.uid)) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        key={index}
                        alt={vote.userName || vote.userId}
                        sx={{ bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF' }}
                        src={getUserPhotoURL(vote?.uid)}
                      />
                      <Typography
                        variant="body2"
                        color="primary"
                        component="small"
                        sx={{ color: '#4C4C4C' }}
                      >
                        {vote.userName}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      key={index}
                      alt={vote.userName || vote.userId}
                      sx={{ bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF' }}
                    >
                      <Typography
                        variant="factorA"
                        color="primary"
                        component="small"
                        sx={{ color: '#4C4C4C' }}
                      >
                        {initial}
                      </Typography>
                    </Avatar>
                    <Typography
                      variant="body2"
                      color="primary"
                      component="small"
                      sx={{ color: '#4C4C4C' }}
                    >
                      {vote.userName}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        ))}
        {currentUser && gameData.createdBy === currentUser.uid && (
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            disabled={!hasDateBeenSelected || isLoading}
            onClick={handleConfirmDate}
          >
            Select Date
          </Button>
        )}
        {/* {(!currentUser || gameData.createdBy !== currentUser.uid) && (
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="containedWagl" sx={{ flex: 1 }} disabled={false} onClick={handleVote}>
              Vote{' '}
            </Button>
            <Button
              variant="outlined"
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '8px',
                width: '398.5px',
                height: '51px',
                flex: 1,
                order: 1,
                borderColor: `#E3DCE7`,
              }}
            >
              {`🚫 Decline Event`}
            </Button>
          </Stack>
        )} */}
      </Stack>
    </Paper>
  );
};

export default GamePoll;
